/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { auth, db, functions } from "../firebase";

function normalizeInviteError(err) {
  const code = String(err?.code || "").toLowerCase();
  const msg = String(err?.message || "");

  if (code.includes("already-exists")) return "This invite has already been used.";
  if (code.includes("not-found")) return "This invite could not be found.";
  if (code.includes("permission-denied")) return "This invite is for a different email address.";

  if (code.includes("failed-precondition")) {
    if (msg.toLowerCase().includes("expired")) return "This invite has expired.";
    return "This invite can no longer be used.";
  }

  if (
    code === "auth/invalid-credential" ||
    code === "auth/wrong-password" ||
    code === "auth/invalid-login-credentials"
  ) {
    return "This email already has an account, but the password entered is incorrect.";
  }

  if (code === "auth/email-already-in-use") {
    return "This email already has an account. Sign in to accept the invite.";
  }

  if (code === "auth/weak-password") {
    return "Choose a stronger password with at least 6 characters.";
  }

  if (code === "auth/too-many-requests") {
    return "Too many attempts. Please wait a moment and try again.";
  }

  return err?.message || "Failed to accept invite.";
}

function getReasonCopy(reason) {
  const value = String(reason || "").toLowerCase();

  if (value.includes("expired")) {
    return {
      title: "This invitation has expired",
      text: "Please contact the school office and ask them to send a new PresentPlus parent invitation.",
    };
  }

  if (value.includes("missing")) {
    return {
      title: "Invitation link is incomplete",
      text: "The invitation token is missing. Please open the link directly from the email sent by your school.",
    };
  }

  return {
    title: "Invite unavailable",
    text: "This parent invitation could not be verified. Please contact the school if you think this is a mistake.",
  };
}

function cleanSchoolName(value) {
  const text = String(value || "").trim();
  if (!text) return "";

  const lowered = text.toLowerCase();
  if (
    lowered === "your school" ||
    lowered === "school" ||
    lowered === "presentplus" ||
    lowered === "undefined" ||
    lowered === "null"
  ) {
    return "";
  }

  return text;
}

function pickSchoolId(...sources) {
  for (const source of sources) {
    if (!source) continue;

    const value =
      source.schoolId ||
      source.sid ||
      source.school?.schoolId ||
      source.school?.id ||
      source.invite?.schoolId ||
      source.invite?.sid;

    const clean = String(value || "").trim();
    if (clean) return clean;
  }

  return "";
}

function pickSchoolName(...sources) {
  const parentNames = new Set();
  const parentEmails = new Set();

  sources.forEach((source) => {
    if (!source) return;

    [
      source.name,
      source.parentName,
      source.parentFullName,
      source.parentDisplayName,
      source.inviteeName,
      source.invitedName,
      source.displayName,
      source.invite?.name,
      source.invite?.parentName,
      source.invite?.parentFullName,
      source.invite?.displayName,
    ].forEach((value) => {
      const clean = String(value || "").trim().toLowerCase();
      if (clean) parentNames.add(clean);
    });

    [
      source.email,
      source.parentEmail,
      source.invitedEmail,
      source.inviteeEmail,
      source.invite?.email,
      source.invite?.parentEmail,
      source.invite?.invitedEmail,
    ].forEach((value) => {
      const clean = String(value || "").trim().toLowerCase();
      if (clean) parentEmails.add(clean);
    });
  });

  const candidates = [];

  sources.forEach((source) => {
    if (!source) return;

    candidates.push(
      source.schoolName,
      source.schoolDisplayName,
      source.schoolTitle,
      source.organisationSchoolName,
      source.organizationSchoolName,
      source.school?.schoolName,
      source.school?.name,
      source.school?.displayName,
      source.schoolBranding?.schoolName,
      source.schoolBranding?.name,
      source.branding?.schoolName,
      source.branding?.schoolDisplayName,
      source.settings?.schoolName,
      source.invite?.schoolName,
      source.invite?.schoolDisplayName,
      source.invite?.school?.schoolName,
      source.invite?.school?.name,
      source.invite?.branding?.schoolName
    );
  });

  const found = candidates.find((value) => {
    const text = cleanSchoolName(value);
    if (!text) return false;

    const lower = text.toLowerCase();
    if (parentNames.has(lower)) return false;
    if (parentEmails.has(lower)) return false;
    if (lower.includes("@")) return false;

    return true;
  });

  return cleanSchoolName(found);
}

async function fetchSchoolNameFromFirestore(schoolId) {
  if (!schoolId) return "";

  try {
    const settingsSnap = await getDoc(doc(db, "schools", schoolId, "settings", "school"));

    if (settingsSnap.exists()) {
      const settings = settingsSnap.data() || {};
      const fromSettings = pickSchoolName({
        schoolName:
          settings.schoolName ||
          settings.name ||
          settings.displayName ||
          settings.title,
      });
      if (fromSettings) return fromSettings;
    }
  } catch (err) {
    console.warn("Could not read school settings for invite branding:", err);
  }

  try {
    const schoolSnap = await getDoc(doc(db, "schools", schoolId));

    if (schoolSnap.exists()) {
      const school = schoolSnap.data() || {};
      const fromSchool = pickSchoolName({
        schoolName:
          school.schoolName ||
          school.name ||
          school.displayName ||
          school.title,
      });
      if (fromSchool) return fromSchool;
    }
  } catch (err) {
    console.warn("Could not read school record for invite branding:", err);
  }

  return "";
}

function PresentPlusMark({ tone = "blue" }) {
  const src =
    tone === "white" ? "/presentplus-logo-white.png" : "/presentplus-logo.png";

  return (
    <img
      src={src}
      alt="PresentPlus"
      style={{
        ...styles.logoImage,
        ...(tone === "white" ? styles.logoImageWhite : {}),
      }}
    />
  );
}

function LoadingScreen() {
  return (
    <div style={styles.wrap} className="pp-invite-wrap">
      <style>{css}</style>
      <div style={styles.bgOrbOne} />
      <div style={styles.bgOrbTwo} />
      <div style={styles.loadingCard}>
        <PresentPlusMark />
        <div style={styles.loadingTitle}>Checking your invitation...</div>
        <div style={styles.loadingDots}>
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}

function AccountReadyScreen({ email }) {
  return (
    <div style={styles.wrap} className="pp-invite-wrap">
      <style>{css}</style>
      <div style={styles.bgOrbOne} />
      <div style={styles.bgOrbTwo} />
      <div style={styles.bgGrid} />

      <main style={styles.readyShell}>
        <section style={styles.readyCard}>
          <div style={styles.readyLogoWrap}>
            <PresentPlusMark />
          </div>

          <div style={styles.readyBadge}>Account ready</div>

          <h1 style={styles.readyTitle}>
            Your PresentPlus parent account is ready
          </h1>

          <p style={styles.readyText}>
            Download or open the PresentPlus app and sign in using the email
            address and password you just created.
          </p>

          <div style={styles.readyEmailCard}>
            <div style={styles.readyEmailLabel}>Your sign-in email</div>
            <div style={styles.readyEmailValue}>{email || "Your invited email"}</div>
          </div>

          <div style={styles.readySteps}>
            <div style={styles.readyStep}>
              <span style={styles.readyStepNumber}>1</span>
              <div>
                <strong>Open or install PresentPlus</strong>
                <p>
                  If the app is already installed, open it from your phone’s home
                  screen. Otherwise, download it using your school’s app link.
                </p>
              </div>
            </div>

            <div style={styles.readyStep}>
              <span style={styles.readyStepNumber}>2</span>
              <div>
                <strong>Sign in once</strong>
                <p>
                  Use the email above and the password you created on this page.
                </p>
              </div>
            </div>

            <div style={styles.readyStep}>
              <span style={styles.readyStepNumber}>3</span>
              <div>
                <strong>Complete setup in the app</strong>
                <p>
                  PresentPlus will recognise this as a new parent account and
                  guide you through the final onboarding before opening Home.
                </p>
              </div>
            </div>
          </div>

          <div style={styles.readyNote}>
            You can now close this browser page. Native notifications and
            biometric setup will happen securely inside the PresentPlus app.
          </div>
        </section>
      </main>
    </div>
  );
}

export default function AcceptParentInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [inviteState, setInviteState] = useState({ ok: false, reason: null });
  const [schoolName, setSchoolName] = useState("your school");

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [mode, setMode] = useState("signup");
  const [accountExists, setAccountExists] = useState(false);
  const [checkingAccount, setCheckingAccount] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signingOutWrongUser, setSigningOutWrongUser] = useState(false);
  const [activeAuthUser, setActiveAuthUser] = useState(() => auth.currentUser || null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [accountReady, setAccountReady] = useState(false);
  const [readyEmail, setReadyEmail] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setActiveAuthUser(user || null);
    });

    return () => unsubscribe();
  }, []);

  const invitedEmail = useMemo(
    () => String(invite?.email || email || "").trim().toLowerCase(),
    [invite, email]
  );

  const activeEmail = String(activeAuthUser?.email || "").trim().toLowerCase();

  const signedInAsDifferentUser = Boolean(
    activeAuthUser && invitedEmail && activeEmail && activeEmail !== invitedEmail
  );

  async function signOutDifferentUser() {
    try {
      setSigningOutWrongUser(true);
      setError("");
      setSuccess("");
      setPassword("");
      await signOut(auth);
      setSuccess("Signed out. You can now continue with the invited email.");
    } catch (err) {
      console.error("Could not sign out current user:", err);
      setError("Could not sign out the current account. Please close this tab and open the invite in a private window.");
    } finally {
      setSigningOutWrongUser(false);
    }
  }

  useEffect(() => {
    let live = true;

    async function run() {
      try {
        setLoading(true);
        setError("");

        if (!token) {
          setInviteState({ ok: false, reason: "missing-token" });
          return;
        }

        const previewFn = httpsCallable(functions, "previewParentInvite");
        const res = await previewFn({ token });

        if (!live) return;

        const data = res?.data || {};
        const inviteData = data.invite || null;

        setInvite(inviteData);
        setInviteState({ ok: !!data.valid, reason: data.reason });

        if (inviteData) {
          setEmail(inviteData.email || "");
          setFullName(inviteData.name || "");

          const directSchoolName = pickSchoolName(inviteData, data);
          const schoolId = pickSchoolId(inviteData, data);

          if (directSchoolName) {
            setSchoolName(directSchoolName);
          } else if (schoolId) {
            const fetchedSchoolName = await fetchSchoolNameFromFirestore(schoolId);
            setSchoolName(fetchedSchoolName || "your school");
          } else {
            setSchoolName("your school");
          }
        }
      } catch (err) {
        console.error("previewParentInvite error:", err);
        setInviteState({ ok: false, reason: "load-error" });
        setError("Failed to load invite.");
      } finally {
        if (live) setLoading(false);
      }
    }

    run();
    return () => {
      live = false;
    };
  }, [token]);

  useEffect(() => {
    let live = true;

    async function check() {
      if (!invite?.email) return;

      try {
        setCheckingAccount(true);

        const methods = await fetchSignInMethodsForEmail(
          auth,
          String(invite.email || "").toLowerCase()
        );

        if (!live) return;

        const exists = methods.length > 0;
        setAccountExists(exists);
        setMode(exists ? "signin" : "signup");
      } catch (err) {
        console.error("fetchSignInMethodsForEmail error:", err);
      } finally {
        if (live) setCheckingAccount(false);
      }
    }

    check();
    return () => {
      live = false;
    };
  }, [invite]);

  async function completeInvite(user, { isNewAccount = false } = {}) {
    const authEmail = String(user?.email || "").trim().toLowerCase();
    const inviteEmail = String(invite?.email || "").trim().toLowerCase();

    if (!authEmail || authEmail !== inviteEmail) {
      throw new Error("This invite is for a different email address.");
    }

    if (fullName?.trim()) {
      await updateProfile(user, { displayName: fullName.trim() });
    }

    const acceptFn = httpsCallable(functions, "acceptParentInviteV2");

    await acceptFn({
      token,
      uid: user.uid,
    });

    /*
      Parent onboarding is controlled by the backend acceptParentInviteV2 function.
      Do not write onboarding flags from the browser here because Firestore rules
      intentionally restrict parent profile security fields.
    */

    try {
      await user.getIdToken(true);
    } catch (tokenErr) {
      console.warn("Could not refresh parent token after invite acceptance:", tokenErr);
    }

    /*
      Locked browser-first flow:
      - The browser finishes account creation and child linking.
      - It does not enter the parent dashboard or native onboarding.
      - The parent opens/downloads the installed app and signs in once.
      - The app detects onboardingCompleted === false and starts native onboarding.
    */
    setReadyEmail(authEmail);
    setPassword("");
    setSuccess("");

    try {
      await signOut(auth);
    } catch (signOutErr) {
      console.warn("Browser session could not be cleared after setup:", signOutErr);
    }

    setAccountReady(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);

    try {
      if (signedInAsDifferentUser) {
        setError("You are signed in as a different account. Please sign out and continue with the invited email.");
        return;
      }

      let cred;
      const cleanEmail = String(email || "").trim().toLowerCase();

      let isNewAccount = false;

      if (mode === "signup") {
        try {
          cred = await createUserWithEmailAndPassword(auth, cleanEmail, password);
          isNewAccount = true;
        } catch (err) {
          if (err.code === "auth/email-already-in-use") {
            setMode("signin");
            cred = await signInWithEmailAndPassword(auth, cleanEmail, password);
            isNewAccount = false;
          } else {
            throw err;
          }
        }
      } else {
        cred = await signInWithEmailAndPassword(auth, cleanEmail, password);
        isNewAccount = false;
      }

      await completeInvite(cred.user, { isNewAccount });
    } catch (err) {
      console.error("accept parent invite error:", err);
      setError(normalizeInviteError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <LoadingScreen />;

  if (accountReady) {
    return <AccountReadyScreen email={readyEmail || invitedEmail} />;
  }

  if (!inviteState.ok) {
    const copy = getReasonCopy(inviteState.reason);

    return (
      <div style={styles.wrap} className="pp-invite-wrap">
        <style>{css}</style>
        <div style={styles.bgOrbOne} />
        <div style={styles.bgOrbTwo} />
        <main style={styles.shellCompact}>
          <section style={styles.unavailableCard}>
            <PresentPlusMark />
            <h1 style={styles.unavailableTitle}>{copy.title}</h1>
            <p style={styles.unavailableText}>{error || copy.text}</p>
            <button
              type="button"
              style={styles.secondaryLargeButton}
              onClick={() => navigate("/")}
            >
              Back to PresentPlus
            </button>
          </section>
        </main>
      </div>
    );
  }

  const primaryButtonLabel = submitting
    ? "Setting up..."
    : mode === "signin"
      ? "Sign in and accept invite"
      : "Create parent account";

  return (
    <div style={styles.wrap} className="pp-invite-wrap">
      <style>{css}</style>
      <div style={styles.bgOrbOne} />
      <div style={styles.bgOrbTwo} />
      <div style={styles.bgGrid} />

      <main style={styles.shell} className="pp-invite-shell">
        <section style={styles.copyPanel} className="pp-invite-copy-panel">
          <div style={styles.brandRow}>
            <PresentPlusMark tone="white" />
            <div>
              <div style={styles.brandName}>PresentPlus</div>
              <div style={styles.brandSub}>Secure parent access</div>
            </div>
          </div>

          <div style={styles.heroBadge}>School invitation</div>

          <h1 style={styles.heroTitle}>Set up your parent access</h1>
          <p style={styles.heroText}>
            Create your secure account to view your child’s attendance, updates,
            absence messages and school reports from {schoolName}.
          </p>

          <div style={styles.schoolCard}>
            <div style={styles.schoolIcon}>🏫</div>
            <div>
              <div style={styles.schoolLabel}>Invited by</div>
              <div style={styles.schoolName}>{schoolName}</div>
            </div>
          </div>

          <div style={styles.promiseGrid}>
            <div style={styles.promiseItem}>
              <span>✓</span>
              Attendance and reports
            </div>
            <div style={styles.promiseItem}>
              <span>✓</span>
              School updates
            </div>
            <div style={styles.promiseItem}>
              <span>✓</span>
              Private family access
            </div>
          </div>
        </section>

        <section style={styles.card} className="pp-invite-card">
          <div style={styles.mobileBrand} className="pp-invite-mobile-brand">
            <PresentPlusMark />
            <div style={styles.mobileBrandText}>PresentPlus</div>
          </div>

          <div style={styles.cardHeader}>
            <div style={styles.cardEyebrow}>Parent onboarding</div>
            <h2 style={styles.cardTitle}>
              {mode === "signin" ? "Accept your invitation" : "Create your account"}
            </h2>
            <p style={styles.cardText}>
              {checkingAccount
                ? "Checking whether this email already has an account..."
                : accountExists
                  ? "This email already has an account. Sign in below to connect the invitation."
                  : "Use the invited email below and choose a password for parent access."}
            </p>
          </div>

          <div style={styles.inviteEmailBox}>
            <div style={styles.fieldIcon}>✉</div>
            <div>
              <div style={styles.inviteEmailLabel}>Invited email</div>
              <div style={styles.inviteEmailValue}>{invite?.email}</div>
            </div>
          </div>

          {signedInAsDifferentUser ? (
            <div style={styles.wrongAccountBox}>
              <div style={styles.wrongAccountIcon}>!</div>
              <div style={styles.wrongAccountCopy}>
                <strong>You are signed in as a different account.</strong>
                <span>
                  This invite is for {invite?.email}, but this browser is currently signed in as {activeAuthUser?.email || "another account"}.
                </span>
                <button
                  type="button"
                  style={styles.wrongAccountButton}
                  onClick={signOutDifferentUser}
                  disabled={signingOutWrongUser}
                >
                  {signingOutWrongUser ? "Signing out..." : "Sign out and continue"}
                </button>
              </div>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} style={styles.form}>
            <label style={styles.fieldLabel} htmlFor="parent-full-name">
              Parent name
            </label>
            <input
              id="parent-full-name"
              name="parentName"
              autoComplete="name"
              className="pp-invite-input"
              style={{ ...styles.input, ...styles.inputDisabled }}
              placeholder="Parent name"
              value={fullName}
              disabled
              readOnly
            />

            <label style={styles.fieldLabel} htmlFor="parent-email">
              School-registered email
            </label>
            <input
              className="pp-invite-input"
              id="parent-email"
              name="email"
              type="email"
              autoComplete="email"
              style={{ ...styles.input, ...styles.inputDisabled }}
              value={email}
              disabled
            />

            <label style={styles.fieldLabel} htmlFor="parent-password">
              Password
            </label>
            <div style={styles.passwordWrap}>
              <input
                id="parent-password"
                name="password"
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                style={styles.passwordInput}
                placeholder={mode === "signin" ? "Enter your password" : "Create a password"}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                style={styles.showButton}
                onClick={() => setShowPassword((value) => !value)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>

            <div style={styles.formRow}>
              <div style={styles.passwordHint}>
                {mode === "signup" ? "Minimum 6 characters." : "Use your existing PresentPlus password."}
              </div>

              <button
                type="button"
                style={styles.linkButton}
                onClick={() => navigate("/forgot-password")}
              >
                Forgot password?
              </button>
            </div>

            {error ? <div style={styles.error}>{error}</div> : null}
            {success ? <div style={styles.success}>{success}</div> : null}

            <button
              type="submit"
              style={{
                ...styles.button,
                ...(submitting ? styles.buttonDisabled : {}),
              }}
              disabled={submitting || checkingAccount || signedInAsDifferentUser}
            >
              {signedInAsDifferentUser ? "Sign out required" : primaryButtonLabel}
            </button>

            <div style={styles.securityNote}>
              <span style={styles.lockIcon}>🔒</span>
              Your access is linked only to the child records approved by the school.
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

const css = `
  @keyframes ppFadeUp {
    from { opacity: 0; transform: translateY(18px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  @keyframes ppPulse {
    0%, 100% { transform: scale(0.85); opacity: 0.42; }
    50% { transform: scale(1); opacity: 1; }
  }

  .pp-invite-input:focus {
    border-color: #2563eb !important;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12) !important;
  }

  .pp-invite-wrap span { box-sizing: border-box; }

  .pp-invite-wrap [style*="display: flex"] > span {
    flex-shrink: 0;
  }

  .pp-invite-wrap div[style*="gap: 8px"] span {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #2563eb;
    animation: ppPulse 1.1s ease-in-out infinite;
  }

  .pp-invite-wrap div[style*="gap: 8px"] span:nth-child(2) { animation-delay: 0.16s; }
  .pp-invite-wrap div[style*="gap: 8px"] span:nth-child(3) { animation-delay: 0.32s; }

  @media (max-width: 920px) {
    .pp-invite-shell {
      grid-template-columns: 1fr !important;
      max-width: 560px !important;
      padding: 22px !important;
    }

    .pp-invite-copy-panel {
      display: none !important;
    }

    .pp-invite-mobile-brand {
      display: flex !important;
    }
  }

  @media (max-width: 560px) {
    html,
    body,
    #root {
      min-height: 100% !important;
      height: 100% !important;
      overflow: hidden !important;
    }

    .pp-invite-wrap {
      min-height: 100dvh !important;
      height: 100dvh !important;
      padding:
        calc(12px + env(safe-area-inset-top, 0px))
        12px
        calc(150px + env(safe-area-inset-bottom, 0px))
        !important;
      align-items: flex-start !important;
      overflow-x: hidden !important;
      overflow-y: auto !important;
      -webkit-overflow-scrolling: touch !important;
      overscroll-behavior-y: contain !important;
      touch-action: pan-y !important;
    }

    .pp-invite-shell {
      padding: 0 !important;
      align-items: stretch !important;
      min-height: auto !important;
    }

    .pp-invite-card {
      width: 100% !important;
      border-radius: 28px !important;
      padding: 22px !important;
      margin: 0 !important;
    }
  }
`;

const styles = {
  readyShell: {
    width: "100%",
    maxWidth: 620,
    position: "relative",
    zIndex: 2,
    margin: "0 auto",
    paddingBottom: "24px",
    animation: "ppFadeUp 0.55s ease both",
  },

  readyCard: {
    width: "100%",
    boxSizing: "border-box",
    padding: "42px 38px",
    borderRadius: 38,
    textAlign: "center",
    background: "rgba(255,255,255,0.88)",
    border: "1px solid rgba(255,255,255,0.90)",
    boxShadow: "0 30px 90px rgba(15,23,42,0.16)",
    backdropFilter: "blur(26px)",
    WebkitBackdropFilter: "blur(26px)",
  },

  readyLogoWrap: {
    width: 118,
    height: 118,
    margin: "0 auto 20px",
    borderRadius: 32,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(145deg,#eff6ff,#eef2ff)",
    border: "1px solid #dbe7ff",
    boxShadow: "0 18px 40px rgba(37,99,235,0.14)",
  },

  readyBadge: {
    display: "inline-flex",
    padding: "9px 14px",
    borderRadius: 999,
    background: "#eef2ff",
    color: "#4338ca",
    fontSize: 12,
    fontWeight: 950,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
  },

  readyTitle: {
    margin: "20px auto 0",
    maxWidth: 520,
    color: "#0f172a",
    fontSize: 42,
    lineHeight: 1.02,
    letterSpacing: "-0.055em",
    fontWeight: 950,
  },

  readyText: {
    margin: "18px auto 0",
    maxWidth: 520,
    color: "#64748b",
    fontSize: 17,
    lineHeight: 1.65,
    fontWeight: 700,
  },

  readyEmailCard: {
    marginTop: 24,
    padding: "16px 18px",
    borderRadius: 20,
    textAlign: "left",
    background: "linear-gradient(135deg,#eff6ff,#eef2ff)",
    border: "1px solid #dbe7ff",
  },

  readyEmailLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: "0.13em",
    textTransform: "uppercase",
  },

  readyEmailValue: {
    marginTop: 5,
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 900,
    wordBreak: "break-word",
  },

  readySteps: {
    marginTop: 24,
    display: "grid",
    gap: 12,
    textAlign: "left",
  },

  readyStep: {
    display: "grid",
    gridTemplateColumns: "42px 1fr",
    gap: 13,
    alignItems: "start",
    padding: 16,
    borderRadius: 20,
    background: "#ffffff",
    border: "1px solid #e6edf8",
  },

  readyStepNumber: {
    width: 42,
    height: 42,
    borderRadius: 15,
    display: "grid",
    placeItems: "center",
    color: "#ffffff",
    background: "linear-gradient(135deg,#2563eb,#4f46e5)",
    fontWeight: 950,
    boxShadow: "0 10px 24px rgba(37,99,235,0.20)",
  },

  readyNote: {
    marginTop: 20,
    padding: "15px 16px",
    borderRadius: 18,
    color: "#475569",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    fontSize: 13,
    lineHeight: 1.55,
    fontWeight: 750,
  },

  wrap: {
    minHeight: "100dvh",
    height: "100dvh",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding:
      "calc(18px + env(safe-area-inset-top, 0px)) 18px calc(150px + env(safe-area-inset-bottom, 0px))",
    boxSizing: "border-box",
    background:
      "radial-gradient(circle at 22% 10%, rgba(37,99,235,0.18), transparent 34%), radial-gradient(circle at 82% 18%, rgba(124,58,237,0.16), transparent 30%), linear-gradient(135deg,#eef4ff 0%, #f8fbff 52%, #ecfeff 100%)",
    position: "relative",
    overflowX: "hidden",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    overscrollBehaviorY: "contain",
  },

  bgOrbOne: {
    position: "absolute",
    width: 520,
    height: 520,
    borderRadius: "50%",
    top: -260,
    right: -160,
    background: "linear-gradient(135deg, rgba(37,99,235,0.28), rgba(124,58,237,0.18))",
    filter: "blur(8px)",
  },

  bgOrbTwo: {
    position: "absolute",
    width: 420,
    height: 420,
    borderRadius: "50%",
    bottom: -260,
    left: -130,
    background: "linear-gradient(135deg, rgba(14,165,233,0.2), rgba(37,99,235,0.16))",
    filter: "blur(10px)",
  },

  bgGrid: {
    position: "absolute",
    inset: 0,
    opacity: 0.42,
    backgroundImage:
      "linear-gradient(rgba(37,99,235,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.045) 1px, transparent 1px)",
    backgroundSize: "44px 44px",
    maskImage: "radial-gradient(circle at center, black, transparent 72%)",
  },

  shell: {
    width: "100%",
    maxWidth: 1080,
    position: "relative",
    zIndex: 2,
    display: "grid",
    gridTemplateColumns: "1.05fr 0.95fr",
    gap: 28,
    alignItems: "stretch",
    animation: "ppFadeUp 0.55s ease both",
  },

  shellCompact: {
    width: "100%",
    maxWidth: 480,
    position: "relative",
    zIndex: 2,
    animation: "ppFadeUp 0.55s ease both",
  },

  copyPanel: {
    minHeight: 600,
    borderRadius: 36,
    padding: 42,
    boxSizing: "border-box",
    color: "#ffffff",
    background:
      "linear-gradient(145deg, #062a59 0%, #0b4ed8 58%, #4f46e5 100%)",
    boxShadow: "0 28px 90px rgba(15, 23, 42, 0.22)",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },

  brandRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    position: "relative",
    zIndex: 2,
  },

  brandName: { fontSize: 22, fontWeight: 900, letterSpacing: "-0.03em" },
  brandSub: { marginTop: 2, fontSize: 13, color: "rgba(255,255,255,0.72)", fontWeight: 700 },

  logoMark: {
    width: 52,
    height: 52,
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg,#2563eb,#4f46e5)",
    boxShadow: "0 16px 34px rgba(37,99,235,0.28)",
    color: "#fff",
    fontWeight: 950,
    position: "relative",
    flexShrink: 0,
  },

  logoP: { fontSize: 28, lineHeight: 1, transform: "translateY(1px)" },
  logoPlus: { position: "absolute", top: 11, right: 10, fontSize: 12, fontWeight: 950 },

  heroBadge: {
    alignSelf: "flex-start",
    marginTop: "auto",
    marginBottom: 20,
    padding: "9px 13px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.22)",
    color: "rgba(255,255,255,0.88)",
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
  },

  heroTitle: {
    margin: 0,
    maxWidth: 520,
    fontSize: 58,
    lineHeight: 0.96,
    letterSpacing: "-0.065em",
    fontWeight: 950,
  },

  heroText: {
    margin: "20px 0 0",
    maxWidth: 520,
    color: "rgba(255,255,255,0.82)",
    fontSize: 17,
    lineHeight: 1.7,
    fontWeight: 650,
  },

  schoolCard: {
    marginTop: 28,
    display: "flex",
    gap: 14,
    alignItems: "center",
    padding: 16,
    borderRadius: 24,
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.22)",
    backdropFilter: "blur(16px)",
  },

  logoImage: {
    width: 96,
    height: "auto",
    objectFit: "contain",
    display: "block",
    margin: "0 auto",
  },

  schoolIcon: {
    width: 48,
    height: 48,
    borderRadius: 18,
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,0.18)",
    fontSize: 22,
  },

  schoolLabel: { fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.72, fontWeight: 900 },
  schoolName: { marginTop: 3, fontSize: 18, fontWeight: 900 },

  promiseGrid: {
    marginTop: 22,
    display: "grid",
    gap: 10,
  },

  promiseItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "rgba(255,255,255,0.88)",
    fontSize: 14,
    fontWeight: 800,
  },

  card: {
    width: "100%",
    borderRadius: 36,
    padding: 34,
    boxSizing: "border-box",
    background: "rgba(255,255,255,0.82)",
    border: "1px solid rgba(255,255,255,0.78)",
    boxShadow: "0 28px 80px rgba(15,23,42,0.16)",
    backdropFilter: "blur(24px)",
    WebkitBackdropFilter: "blur(24px)",
  },

  mobileBrand: {
    display: "none",
    alignItems: "center",
    gap: 12,
    marginBottom: 24,
  },

  mobileBrandText: { fontSize: 20, fontWeight: 950, color: "#10224a" },

  cardHeader: { marginBottom: 22 },
  cardEyebrow: {
    color: "#2563eb",
    fontSize: 12,
    fontWeight: 950,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },
  cardTitle: {
    margin: "8px 0 0",
    color: "#0f172a",
    fontSize: 34,
    lineHeight: 1.05,
    letterSpacing: "-0.045em",
    fontWeight: 950,
  },
  cardText: { margin: "10px 0 0", color: "#667085", lineHeight: 1.6, fontWeight: 650 },

  inviteEmailBox: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    padding: 16,
    borderRadius: 22,
    background: "linear-gradient(135deg,#eff6ff,#eef2ff)",
    border: "1px solid #dbe7ff",
    marginBottom: 20,
  },

  fieldIcon: {
    width: 44,
    height: 44,
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    background: "#ffffff",
    color: "#2563eb",
    fontWeight: 900,
    boxShadow: "0 10px 26px rgba(37,99,235,0.10)",
  },

  inviteEmailLabel: { fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#667085", fontWeight: 950 },
  inviteEmailValue: { marginTop: 4, color: "#0f172a", fontSize: 15, fontWeight: 850, wordBreak: "break-word" },

  form: { display: "grid", gap: 10 },
  fieldLabel: {
    marginTop: 4,
    color: "#334155",
    fontSize: 12,
    fontWeight: 950,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },

  input: {
    width: "100%",
    padding: "15px 16px",
    borderRadius: 17,
    border: "1px solid #d8e1f2",
    background: "rgba(255,255,255,0.94)",
    color: "#0f172a",
    outline: "none",
    fontSize: 15,
    boxSizing: "border-box",
  },

  inputDisabled: {
    background: "#f6f8fc",
    color: "#475569",
    cursor: "not-allowed",
  },

  passwordWrap: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    borderRadius: 17,
    border: "1px solid #d8e1f2",
    background: "rgba(255,255,255,0.94)",
    overflow: "hidden",
  },

  passwordInput: {
    border: "none",
    outline: "none",
    background: "transparent",
    padding: "15px 16px",
    color: "#0f172a",
    fontSize: 15,
    minWidth: 0,
  },

  showButton: {
    height: "100%",
    border: "none",
    borderLeft: "1px solid #e5ecf7",
    background: "#f8fbff",
    color: "#2563eb",
    padding: "0 16px",
    fontWeight: 900,
    cursor: "pointer",
  },

  formRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    marginTop: 2,
  },

  passwordHint: { color: "#667085", fontSize: 13, fontWeight: 700 },
  linkButton: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 13,
    whiteSpace: "nowrap",
  },

  button: {
    marginTop: 8,
    minHeight: 54,
    borderRadius: 18,
    border: "none",
    background: "linear-gradient(135deg,#2563eb,#4f46e5)",
    color: "#fff",
    fontWeight: 950,
    fontSize: 15,
    cursor: "pointer",
    boxShadow: "0 18px 36px rgba(37,99,235,0.26)",
  },

  buttonDisabled: { opacity: 0.7, cursor: "not-allowed" },

  error: {
    padding: "12px 14px",
    borderRadius: 16,
    color: "#b42318",
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    fontWeight: 800,
    lineHeight: 1.45,
  },

  success: {
    padding: "12px 14px",
    borderRadius: 16,
    color: "#027a48",
    background: "#ecfdf5",
    border: "1px solid #abefc6",
    fontWeight: 850,
    lineHeight: 1.45,
  },

  wrongAccountBox: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    padding: 14,
    borderRadius: 18,
    color: "#9f1239",
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    marginBottom: 14,
  },

  wrongAccountIcon: {
    width: 28,
    height: 28,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    background: "#fecdd3",
    color: "#9f1239",
    fontSize: 15,
    fontWeight: 950,
    flex: "0 0 auto",
  },

  wrongAccountCopy: {
    display: "grid",
    gap: 7,
    color: "#9f1239",
    fontSize: 13,
    lineHeight: 1.45,
    fontWeight: 750,
  },

  wrongAccountButton: {
    justifySelf: "start",
    minHeight: 38,
    padding: "0 14px",
    borderRadius: 999,
    border: "none",
    background: "#9f1239",
    color: "#ffffff",
    fontSize: 13,
    fontWeight: 950,
    cursor: "pointer",
  },

  securityNote: {
    display: "flex",
    gap: 9,
    alignItems: "flex-start",
    marginTop: 8,
    color: "#667085",
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 700,
  },

  lockIcon: { color: "#2563eb", transform: "translateY(1px)" },

  loadingCard: {
    position: "relative",
    zIndex: 2,
    width: "min(420px, 92vw)",
    minHeight: 260,
    borderRadius: 34,
    display: "grid",
    placeItems: "center",
    alignContent: "center",
    gap: 16,
    background: "rgba(255,255,255,0.74)",
    border: "1px solid rgba(255,255,255,0.82)",
    boxShadow: "0 28px 80px rgba(15,23,42,0.14)",
    backdropFilter: "blur(24px)",
  },

  loadingTitle: { fontSize: 17, fontWeight: 950, color: "#10224a" },
  loadingDots: { display: "flex", gap: 8 },

  unavailableCard: {
    borderRadius: 34,
    padding: 34,
    textAlign: "center",
    display: "grid",
    justifyItems: "center",
    gap: 16,
    background: "rgba(255,255,255,0.82)",
    border: "1px solid rgba(255,255,255,0.78)",
    boxShadow: "0 28px 80px rgba(15,23,42,0.16)",
    backdropFilter: "blur(24px)",
  },

  unavailableTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: 30,
    lineHeight: 1.1,
    letterSpacing: "-0.04em",
    fontWeight: 950,
  },

  unavailableText: { margin: 0, color: "#667085", lineHeight: 1.6, fontWeight: 700 },
  secondaryLargeButton: {
    marginTop: 4,
    minHeight: 50,
    padding: "0 18px",
    borderRadius: 16,
    border: "1px solid #d8e1f2",
    background: "#ffffff",
    color: "#2563eb",
    fontWeight: 950,
    cursor: "pointer",
  },
};
